import React, { createContext, useContext, useEffect, useState } from "react";
import { firestore } from "../config/firebaseConfig";
import { useAuth } from "./AuthContext";
import { toast } from "react-toastify";

const ProjectContext = createContext();

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    return sessionStorage.getItem("selectedProjectId") || null;
  });
  const [loading, setLoading] = useState(false);
  const [referencePoints, setReferencePoints] = useState([]);
  const [sets, setSets] = useState([]);

  // ── Fetch projects ────────────────────────────────────────────────────────────
  // ── Fetch projects ────────────────────────────────────────────────────────────
  // Every user sees projects they own (uuid) + projects they belong to (team subcollection)
  const fetchProjects = async () => {
    if (!user?.uid) {
      setProjects([]);
      return;
    }
    setLoading(true);
    try {
      const uid = user.uid;
      const email = user.email?.toLowerCase();
      // console.log("Fetching projects for UID:", uid);

      // ── Step 1: projects owned by this user ──────────────────────────
      const ownedSnap = await firestore
        .collection("projects")
        .where("uuid", "==", uid)
        .get();
      const ownedProjects = ownedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const seenIds = new Set(ownedProjects.map((p) => p.id));

      // ── Step 2: projects where user is a team member (by uid) ────────
      const teamByUid = await firestore
        .collectionGroup("team")
        .where("uid", "==", uid)
        .get();

      const teamProjectIds = new Set();
      teamByUid.docs.forEach((doc) => {
        // Path: projects/{projectId}/team/{memberId}
        const projectId = doc.ref.parent.parent.id;
        if (!seenIds.has(projectId)) teamProjectIds.add(projectId);
      });

      // ── Step 3: also match by email (for pending invitations) ────────
      if (email) {
        const teamByEmail = await firestore
          .collectionGroup("team")
          .where("email", "==", email)
          .get();
        teamByEmail.docs.forEach((doc) => {
          const projectId = doc.ref.parent.parent.id;
          if (!seenIds.has(projectId)) teamProjectIds.add(projectId);
        });
      }

      // ── Step 4: fetch those project docs individually ────────────────
      const teamProjects = [];
      for (const pid of teamProjectIds) {
        const doc = await firestore.collection("projects").doc(pid).get();
        if (doc.exists) teamProjects.push({ id: doc.id, ...doc.data() });
      }

      const data = [...ownedProjects, ...teamProjects];

      // console.log("Fetched project data:", data);
      setProjects(data);

      // Collect all IFC files from projects
      const allFiles = [];
      data.forEach((project) => {
        if (project.ifcModels && Array.isArray(project.ifcModels)) {
          project.ifcModels.forEach((model) => {
            allFiles.push({
              projectId: project.id,
              discipline: model.discipline,
              fileName: model.fileName,
              url: model.url,
            });
          });
        }
      });
      setFiles(allFiles);

      // Default to first project if none selected
      if (!sessionStorage.getItem("selectedProjectId") && data.length > 0) {
        const firstId = data[0].id;
        setSelectedProjectId(firstId);
        sessionStorage.setItem("selectedProjectId", firstId);
      }

      // Reset if selected project no longer exists
      if (selectedProjectId && !data.find((p) => p.id === selectedProjectId)) {
        const firstId = data.length > 0 ? data[0].id : null;
        setSelectedProjectId(firstId);
        if (firstId) sessionStorage.setItem("selectedProjectId", firstId);
        else sessionStorage.removeItem("selectedProjectId");
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      toast.error("Failed to load projects: " + (err.message || "Unknown error"));
      setProjects([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch reference points ────────────────────────────────────────────────────
  const fetchReferencePoints = async (projectId) => {
    if (!projectId) {
      setReferencePoints([]);
      return;
    }
    try {
      // console.log("📡 Fetching reference points for project:", projectId);
      const snapshot = await firestore
        .collection("projects")
        .doc(projectId)
        .collection("referencePoints")
        .get();

      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      // console.log("📍 Reference points fetched:", data);
      setReferencePoints(data);
    } catch (error) {
      console.error("❌ Error fetching reference points:", error);
      // Suppress permission errors — they fire before auth is ready
      if (!error.message?.includes("Missing or insufficient permissions")) {
        toast.error("Failed to load reference points: " + (error.message || "Unknown error"));
      }
    }
  };


  // ── Effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    // Only fetch when user is authenticated
    if (!user?.uid) return;
    fetchReferencePoints(selectedProjectId);
  }, [selectedProjectId, user?.uid]);

  // Real-time sets listener — re-subscribes whenever selectedProjectId or user changes
  useEffect(() => {
    // Don't subscribe until user is authenticated
    if (!user?.uid || !selectedProjectId) {
      setSets([]);
      return;
    }

    const unsubscribe = firestore
      .collection("projects")
      .doc(selectedProjectId)
      .collection("sets")
      .onSnapshot(
        (snapshot) => {
          const fetchedSets = snapshot.docs.map((doc) => ({
            id: doc.id,
            name: doc.data().name,
            discipline: doc.data().discipline,
            GlobalIds: doc.data().GlobalIds || [],
          }));
          setSets(fetchedSets);
        },
        (error) => {
          console.error("Error fetching sets:", error);
          // Suppress permission errors — they fire before auth is ready
          if (!error.message?.includes("Missing or insufficient permissions")) {
            toast.error("Error fetching sets: " + error.message);
          }
        }
      );

    return () => unsubscribe();
  }, [selectedProjectId, user?.uid]);

  // ── Project selection helper ──────────────────────────────────────────────────
  const selectProject = (projectId) => {
    setSelectedProjectId(projectId);
    if (projectId) sessionStorage.setItem("selectedProjectId", projectId);
    else sessionStorage.removeItem("selectedProjectId");
  };

  const refreshProjects = () => fetchProjects();

  const assignProjectToCurrentUser = async (projectId) => {
    if (!user?.uid) {
      console.error("User not authenticated");
      return;
    }
    try {
      await firestore.collection("projects").doc(projectId).update({
        uuid: user.uid,
      });
      // console.log(`Project ${projectId} assigned to user ${user.uid}`);
      refreshProjects();
    } catch (err) {
      toast.error("Failed to assign project: " + err.message);
      // console.error("Failed to assign project:", err);
      throw err;
    }
  };

  // ── Provider ──────────────────────────────────────────────────────────────────
  return (
    <ProjectContext.Provider
      value={{
        projects,
        files,
        selectedProjectId,
        selectProject,
        refreshProjects,
        loading,
        assignProjectToCurrentUser,
        referencePoints,
        sets,             // ← available to all consumers
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProjects must be used within ProjectProvider");
  return ctx;
};